import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { ConversationSession, LessonContent, CreateConversationRequest, SessionCompletionData, CompleteSessionData } from '@/services/conversation';
import { get } from 'lodash';

// Async actions for saga
export const createConversationSession = createAction<CreateConversationRequest>(
    'conversationSession/createConversationSession',
);
export const createConversationSessionSuccess = createAction<ConversationSession>(
    'conversationSession/createConversationSessionSuccess',
);
export const createConversationSessionFailure = createAction<string>(
    'conversationSession/createConversationSessionFailure',
);

export const fetchConversationSession = createAction<{ sessionId: string }>(
    'conversationSession/fetchConversationSession',
);
export const fetchConversationSessionSuccess = createAction<ConversationSession>(
    'conversationSession/fetchConversationSessionSuccess',
);
export const fetchConversationSessionFailure = createAction<string>(
    'conversationSession/fetchConversationSessionFailure',
);

export const updateConversationSession = createAction<{ sessionId: string; data: Partial<ConversationSession> }>(
    'conversationSession/updateConversationSession',
);
export const updateConversationSessionSuccess = createAction<ConversationSession>(
    'conversationSession/updateConversationSessionSuccess',
);
export const updateConversationSessionFailure = createAction<string>(
    'conversationSession/updateConversationSessionFailure',
);

export const completeConversationSession = createAction<{ sessionId: string, sessionData: ConversationSession }>(
    'conversationSession/completeConversationSession',
);
export const completeConversationSessionSuccess = createAction<SessionCompletionData>(
    'conversationSession/completeConversationSessionSuccess',
);
export const completeConversationSessionFailure = createAction<string>(
    'conversationSession/completeConversationSessionFailure',
);

export const fetchSessionLessons = createAction<{ sessionId: string }>('conversationSession/fetchSessionLessons');
export const fetchSessionLessonsSuccess = createAction<LessonContent[]>(
    'conversationSession/fetchSessionLessonsSuccess',
);
export const fetchSessionLessonsFailure = createAction<string>('conversationSession/fetchSessionLessonsFailure');

export const startLesson = createAction<{ lessonId: string }>('conversationSession/startLesson');
export const startLessonSuccess = createAction<LessonContent>('conversationSession/startLessonSuccess');
export const startLessonFailure = createAction<string>('conversationSession/startLessonFailure');

export const completeLesson = createAction<{
    lessonId: string;
    completionData: { completion_score?: number; time_spent?: string; lesson_data?: Record<string, any> };
}>('conversationSession/completeLesson');
export const completeLessonSuccess = createAction<LessonContent>('conversationSession/completeLessonSuccess');
export const completeLessonFailure = createAction<string>('conversationSession/completeLessonFailure');

export const fetchUserSessions = createAction('conversationSession/fetchUserSessions');
export const fetchUserSessionsSuccess = createAction<ConversationSession[]>(
    'conversationSession/fetchUserSessionsSuccess',
);
export const fetchUserSessionsFailure = createAction<string>('conversationSession/fetchUserSessionsFailure');

export interface ConversationSessionState {
    currentSession: ConversationSession | null;
    completeSessionData: SessionCompletionData | null;
    userSessions: ConversationSession[];
    recentSessions: ConversationSession[];
    sessionLessons: LessonContent[];
    answeredQuestions: Record<
        string,
        {
            id?: string;
            text: string;
            score: number;
        }
    >;
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isCompleting: boolean;
    isLoadingLessons: boolean;
    error: string | null;
    lastUpdated: string | null;
}

const initialState: ConversationSessionState = {
    currentSession: null,
    completeSessionData: null,
    userSessions: [],
    recentSessions: [],
    sessionLessons: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isLoadingLessons: false,
    error: null,
    lastUpdated: null,
    answeredQuestions: {},
};

const conversationSessionSlice = createSlice({
    name: 'conversationSession',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => initialState,
        setCurrentSession: (state, action: PayloadAction<ConversationSession>) => {
            state.currentSession = action.payload;
        },
        clearCurrentSession: (state) => {
            state.currentSession = null;
            state.sessionLessons = [];
        },
        updateCurrentSessionData: (state, action: PayloadAction<Partial<ConversationSession>>) => {
            if (state.currentSession) {
                state.currentSession = { ...state.currentSession, ...action.payload };
            }
        },
        updateCompletedActivities: (state, action: PayloadAction<string[]>) => {
            if (state.currentSession) {
                state.currentSession.session_data = {
                    ...state.currentSession.session_data,
                    completed_activities: action.payload,
                };
            }
        },
        updateLessonInSession: (state, action: PayloadAction<LessonContent>) => {
            const lessonIndex = state.sessionLessons.findIndex((lesson) => lesson.id === action.payload.id);
            if (lessonIndex >= 0) {
                state.sessionLessons[lessonIndex] = action.payload;
            }

            // Update in current session if it exists
            if (state.currentSession) {
                const sessionLessonIndex = state.currentSession.lesson_contents?.findIndex(
                    (lesson) => lesson.id === action.payload.id,
                );
                if (
                    sessionLessonIndex !== undefined &&
                    sessionLessonIndex >= 0 &&
                    state.currentSession.lesson_contents
                ) {
                    state.currentSession.lesson_contents[sessionLessonIndex] = action.payload;
                }
            }
        },
        addSessionToHistory: (state, action: PayloadAction<ConversationSession>) => {
            const existingIndex = state.userSessions.findIndex((session) => session.id === action.payload.id);
            if (existingIndex >= 0) {
                state.userSessions[existingIndex] = action.payload;
            } else {
                state.userSessions.unshift(action.payload);
            }

            // Keep only latest 50 sessions
            if (state.userSessions.length > 50) {
                state.userSessions = state.userSessions.slice(0, 50);
            }
        },
        updateAnswers: (
            state,
            action: PayloadAction<{ questionId: string; answer: { id?: string; text: string; score: number } }>,
        ) => {
            if (action.payload && action.payload.questionId && action.payload.answer) {
                state.answeredQuestions[action.payload.questionId] = action.payload.answer;
            }
        },
        resetAnswers: (state) => {
            state.answeredQuestions = {};
        },
        updateSessionData: (state, action: PayloadAction<ConversationSession>) => {
            if (state.currentSession) {
                state.currentSession = {
                    ...state.currentSession,
                    time_spent: action.payload.time_spent
                }
            }
        },
    },
    extraReducers: (builder) => {
        // Create Conversation Session
        builder
            .addCase(createConversationSession, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createConversationSessionSuccess, (state, action) => {
                state.isCreating = false;
                state.currentSession = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;

                // Add to user sessions
                state.userSessions.unshift(action.payload);
            })
            .addCase(createConversationSessionFailure, (state, action) => {
                state.isCreating = false;
                state.error = action.payload;
            });

        // Fetch Conversation Session
        builder
            .addCase(fetchConversationSession, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchConversationSessionSuccess, (state, action) => {
                state.isLoading = false;
                state.currentSession = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchConversationSessionFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Update Conversation Session
        builder
            .addCase(updateConversationSession, (state, action) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateConversationSessionSuccess, (state, action) => {
                state.isUpdating = false;
                if (state.currentSession && get(action, 'payload.data.current_activities')) {
                    state.currentSession.current_activities = get(action, 'payload.data.current_activities');
                }
                if (state.currentSession && get(action, 'payload.data.current_activity_index')) {
                    state.currentSession.current_activity_index = get(action, 'payload.data.current_activity_index');
                }
                state.error = null;
            })
            .addCase(updateConversationSessionFailure, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload;
            });

        // Complete Conversation Session
        builder
            .addCase(completeConversationSession, (state) => {
                state.isCompleting = true;
                state.error = null;
                state.completeSessionData = null; // Clear previous data
            })
            .addCase(completeConversationSessionSuccess, (state, action) => {
                state.isCompleting = false;
                state.completeSessionData = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(completeConversationSessionFailure, (state, action) => {
                state.isCompleting = false;
                state.error = action.payload;
                state.completeSessionData = null;
            });

        // Fetch Session Lessons
        builder
            .addCase(fetchSessionLessons, (state) => {
                state.isLoadingLessons = true;
                state.error = null;
            })
            .addCase(fetchSessionLessonsSuccess, (state, action) => {
                state.isLoadingLessons = false;
                state.sessionLessons = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchSessionLessonsFailure, (state, action) => {
                state.isLoadingLessons = false;
                state.error = action.payload;
            });

        // Start Lesson
        builder
            .addCase(startLesson, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(startLessonSuccess, (state, action) => {
                state.isUpdating = false;

                // Update lesson in sessionLessons
                const lessonIndex = state.sessionLessons.findIndex((lesson) => lesson.id === action.payload.id);
                if (lessonIndex >= 0) {
                    state.sessionLessons[lessonIndex] = action.payload;
                }

                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(startLessonFailure, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload;
            });

        // Complete Lesson
        builder
            .addCase(completeLesson, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(completeLessonSuccess, (state, action) => {
                state.isUpdating = false;

                // Update lesson in sessionLessons
                const lessonIndex = state.sessionLessons.findIndex((lesson) => lesson.id === action.payload.id);
                if (lessonIndex >= 0) {
                    state.sessionLessons[lessonIndex] = action.payload;
                }

                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(completeLessonFailure, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload;
            });

        // Fetch User Sessions
        builder
            .addCase(fetchUserSessions, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserSessionsSuccess, (state, action) => {
                state.isLoading = false;
                state.userSessions = action.payload;
                state.recentSessions = action.payload.slice(0, 10); // Keep 10 most recent

                // Set the first session as current session if it exists and current session is null
                if (action.payload.length > 0 && !state.currentSession) {
                    state.currentSession = action.payload[0];
                }

                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchUserSessionsFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    resetState,
    setCurrentSession,
    clearCurrentSession,
    updateCurrentSessionData,
    updateCompletedActivities,
    updateLessonInSession,
    updateSessionData,
    addSessionToHistory,
    updateAnswers,
    resetAnswers,
} = conversationSessionSlice.actions;

export default conversationSessionSlice.reducer;
